import { AuthzId, Email, FirstName, LastName, UserId } from "@inrev/common";
import {
	ACTIVE_AGENT_FOLLOWER_REQUIRED,
	isActiveAgentPreview,
} from "@portal/api/followers/hasActiveAgentFollower";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { cn } from "@portal/components/lib/utils";
import { Text } from "@portal/components/primitives";
import { useState } from "react";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { HiMiniCheck, HiOutlineUsers } from "react-icons/hi2";

type AgentPreviewOption = {
	id: UserId;
	authzId: AuthzId;
	email: Email;
	displayName: string;
	firstName?: FirstName;
	lastName?: LastName;
	invitationPending?: boolean;
};

export const AdminFormFollowers = ({
	followers,
	agentPreviews,
	error,
	onFollow,
	onUnfollow,
}: {
	followers: UserId[] | undefined;
	agentPreviews: AgentPreviewOption[];
	error?: FieldError | Merge<FieldError, FieldErrorsImpl<UserId[]>>;
	onFollow: (id: UserId) => void;
	onUnfollow: (id: UserId) => void;
}) => {
	const [followersModalOpen, setFollowersModalOpen] = useState(false);

	const onClose = () => setFollowersModalOpen(false);
	const isFollowingArr =
		agentPreviews.map((agent) => {
			if (!followers) {
				return false;
			}
			return followers.some((follower) => follower === agent.id);
		}) || [];

	const activeFollowerIds = (followers ?? []).filter((followerId) => {
		const agent = agentPreviews.find((preview) => preview.id === followerId);
		return !!agent && isActiveAgentPreview(agent);
	});

	return (
		<div className="relative">
			<div
				className={cn(
					"flex items-center justify-center w-fit h-[24px] min-h-[24px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-gray-700 border border-gray-400 bg-white hover:text-gray-800 hover:border-gray-600 rounded-full cursor-pointer",
					error ? "border-red-500" : "",
				)}
				onClick={() => setFollowersModalOpen(true)}
			>
				<span>{followers?.length || 0}</span>
				<HiOutlineUsers className="text-[13px] text-secondary stroke-[1.75] mt-[1px]" />
			</div>
			{error && error.message && (
				<span className="absolute text-red-500 text-[11px] font-normal -top-4 left-0 text-nowrap">
					{error.message}
				</span>
			)}

			{followersModalOpen && (
				<>
					{!(agentPreviews.length > 0) ? (
						<div className="flex items-center px-[8px] text-[12px] text-secondary italic h-[32px] m-[12px] bg-gray-100 animate-pulse rounded-md">
							Loading followers..
						</div>
					) : (
						<Modal onClickOutside={onClose}>
							<ModalItemWithHeader
								onClose={onClose}
								header={
									<div className="flex flex-col space-y-[8px] mb-[4px]">
										<span className="font-[550]">Agent Followers</span>
										<span className="text-[12.5px] text-secondary font-normal max-w-[90%]">
											Followers will receive email notifications for all comments and changes to the
											status
										</span>
									</div>
								}
								className="w-[500px] h-fit max-h-[85vh]"
								bodyClassName="overflow-y-auto"
							>
								{agentPreviews.map((agent, index) => {
									const isFollowing = isFollowingArr[index];
									const isLastActive =
										!!isFollowing &&
										isActiveAgentPreview(agent) &&
										activeFollowerIds.length === 1 &&
										activeFollowerIds[0] === agent.id;

									const button = (
										<_FollowButton
											notFollowingLabel="Set as follower"
											isFollowing={isFollowing}
											onClick={() => {
												if (isFollowing) {
													onUnfollow(agent.id);
												} else {
													onFollow(agent.id);
												}
											}}
										/>
									);

									return (
										<div
											key={agent.id}
											className="flex items-center justify-between space-x-[5px] px-[24px] py-[18px]"
										>
											<Text type="body" color="secondary">
												{agent.displayName}
											</Text>
											{isLastActive ? (
												<ConfirmationModalClickWrapper
													shouldConfirm
													message={ACTIVE_AGENT_FOLLOWER_REQUIRED}
													confirmButtonText="Okay"
													confirmationButtonColor="primary"
													className="items-center font-medium"
													cancelButton={false}
												>
													{button}
												</ConfirmationModalClickWrapper>
											) : (
												button
											)}
										</div>
									);
								})}
							</ModalItemWithHeader>
						</Modal>
					)}
				</>
			)}
		</div>
	);
};

const _FollowButton = ({
	notFollowingLabel,
	isFollowing,
	onClick,
	condensed,
}: {
	notFollowingLabel: string;
	isFollowing: boolean | undefined;
	onClick: () => void;
	condensed?: boolean;
}) => (
	<div
		className={cn(
			"flex items-center justify-center w-fit h-[24px] min-h-[24px] min-w-[50px] leading-[13px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-secondary border hover:text-gray-700 rounded-full cursor-pointer",
			condensed && "h-[22px] min-h-[22px] text-[11.5px] leading-[11px]",
			isFollowing === undefined
				? "animate-pulse"
				: isFollowing
					? "bg-gray-100 border-gray-500"
					: "bg-gray-white border-gray-400 hover:border-gray-500 px-[12px]",
		)}
		onClick={onClick}
	>
		{isFollowing && <HiMiniCheck className="text-tertiary stroke-[1] ml-[-1px] mt-[.5px]" />}
		{isFollowing !== undefined ? (isFollowing ? "Following" : notFollowingLabel) : ""}
	</div>
);
